import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { fmt } from '../utils/gst';
import ConfirmDialog from '../components/ConfirmDialog';
import { fmtDate, getInitialMonth } from '../utils/format';
import SearchableSelect from '../components/SearchableSelect';

function EditModal({ entry, form, setForm, clientId, setClientId, personNodeId, setPersonNodeId, clients, categories, isOwner, users, onSave, onClose, isPending, proofFile, setProofFile, proofPreview, setProofPreview, setProofRemoved }) {
  const client = clients.find(c => c.id === clientId);
  const allNodes = client?.allNodes || [];
  const topNodes = allNodes.filter(n => !n.parent_id);
  const stakeholderNodes = personNodeId ? allNodes.filter(n => n.parent_id === personNodeId) : [];
  const clientCats = categories.filter(c => c.client_id === clientId);
  const globalCats = categories.filter(c => !c.client_id);
  const fileInputRef = useRef(null);

  // Ctrl+V paste to capture screenshot into THIS modal's proof field
  useEffect(() => {
    const handlePaste = e => {
      if (!e.clipboardData?.items) return;
      for (const item of e.clipboardData.items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (!file) continue;
          setProofFile(file);
          setProofPreview(URL.createObjectURL(file));
          setProofRemoved(false);
          toast.success('Screenshot captured as proof');
          e.preventDefault();
          e.stopImmediatePropagation(); // block any page-level paste handler from creating a new entry
          break;
        }
      }
    };
    // capture phase so this runs before any page-level paste handler
    window.addEventListener('paste', handlePaste, true);
    return () => window.removeEventListener('paste', handlePaste, true);
  }, [setProofFile, setProofPreview, setProofRemoved]);

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-title">Edit entry</div>
        <div className="field">
          <label>Client</label>
          <SearchableSelect
            value={clientId}
            onChange={id => {
              setClientId(id);
              setPersonNodeId('');
              // Category is left as-is — hierarchy nodes are client-specific so those reset,
              // but the category shouldn't silently change unless the user picks a new one.
              setForm(f => ({...f, hierarchy_node_id: ''}));
            }}
            items={clients}
            placeholder="Select client"
          />
        </div>
        <div className="field">
          <label>Category</label>
          <SearchableSelect
            value={form.category_id}
            onChange={id => setForm(f => ({...f, category_id: id}))}
            groups={[
              { label: client ? `Only for ${client.name}` : null, items: clientCats },
              { label: client ? 'Global' : null, items: globalCats },
            ]}
            placeholder="Select category"
          />
        </div>
        {topNodes.length > 0 && (
          <div className="form-grid">
            <div className="field">
              <label>Person</label>
              <select value={personNodeId} onChange={ev => {
                const pid = ev.target.value;
                setPersonNodeId(pid);
                const children = allNodes.filter(n => n.parent_id === pid);
                setForm(f => ({...f, hierarchy_node_id: children.length === 0 ? pid : ''}));
              }}>
                <option value="">— None —</option>
                {topNodes.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
              </select>
            </div>
            {personNodeId && stakeholderNodes.length > 0 && (
              <div className="field">
                <label>Stakeholder</label>
                <select value={form.hierarchy_node_id} onChange={ev => setForm(f => ({...f, hierarchy_node_id: ev.target.value}))}>
                  <option value={personNodeId}>— None (person only) —</option>
                  {stakeholderNodes.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
              </div>
            )}
          </div>
        )}
        <div className="field">
          <label>Description</label>
          <input value={form.description} onChange={ev => setForm(f => ({...f, description: ev.target.value}))} placeholder="Description"/>
        </div>
        {isOwner && (
          <div className="field">
            <label>Designer — who actually did the work</label>
            <SearchableSelect
              value={form.logged_by}
              onChange={id => setForm(f => ({...f, logged_by: id}))}
              items={users}
              getId={u => u.id}
              getLabel={u => u.full_name}
              placeholder="Select designer"
            />
          </div>
        )}
        <div className="form-grid">
          <div className="field">
            <label>Date</label>
            <input type="date" value={form.work_date} onChange={ev => setForm(f => ({...f, work_date: ev.target.value}))}/>
          </div>
          <div className="field">
            <label>Quantity</label>
            <input type="number" min="1" value={form.quantity} onChange={ev => setForm(f => ({...f, quantity: parseInt(ev.target.value)||1}))}/>
          </div>
        </div>
        <div className="field">
          <label>Design proof — replace screenshot or image (optional)</label>
          <input ref={fileInputRef} type="file" accept="image/*" style={{display:'none'}}
            onChange={ev => { const f = ev.target.files[0]; if(f){ setProofFile(f); setProofPreview(URL.createObjectURL(f)); setProofRemoved(false); } }}/>
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <button type="button" className="btn-sm" onClick={() => fileInputRef.current?.click()}>Upload image</button>
            <span style={{fontSize:11,color:'var(--text3)'}}>or Ctrl+V to paste a screenshot</span>
            {proofPreview && (
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <img src={proofPreview} alt="proof" style={{height:48,borderRadius:6,border:'1px solid var(--border)',objectFit:'cover'}}/>
                <span style={{fontSize:11,color:'var(--text2)'}}>{proofFile ? 'New image selected' : 'Current proof'}</span>
                <button type="button" className="btn-sm" title="Remove proof" style={{padding:'2px 7px'}}
                  onClick={() => { setProofFile(null); setProofPreview(null); setProofRemoved(true); }}>✕</button>
              </div>
            )}
            {!proofPreview && <span style={{fontSize:11,color:'var(--text2)'}}>No proof attached</span>}
          </div>
        </div>
        <div className="modal-actions">
          <button onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={onSave} disabled={isPending}>
            {isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WorkLogPage() {
  const { isOwner, user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState([]);
  const [month, setMonth] = useState(getInitialMonth);
  const [filters, setFilters] = useState({ search:'', client:'', category:'', type:'' });
  const [useRange, setUseRange] = useState(false);
  const [fromMonth, setFromMonth] = useState(month);
  const [toMonth, setToMonth] = useState(month);

  // Correction modal — supports multiple lines
  const [corrModal, setCorrModal] = useState(null);
  const [corrLines, setCorrLines] = useState([{ correction_type_id:'', correction_count:1, reason:'' }]);

  // Edit entry modal
  const [editModal, setEditModal] = useState(null);
  const [editClientId, setEditClientId] = useState('');
  const [editForm, setEditForm] = useState({ description:'', quantity:1, work_date:'', category_id:'', hierarchy_node_id:'', logged_by:'' });
  const [editPersonNodeId, setEditPersonNodeId] = useState('');
  const [editProofFile, setEditProofFile] = useState(null);
  const [editProofPreview, setEditProofPreview] = useState(null);
  const [editProofRemoved, setEditProofRemoved] = useState(false);

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Duplicate review
  const [dupReview, setDupReview] = useState(false);
  const [selectedDups, setSelectedDups] = useState([]);

  // Proof image
  const [previewImg, setPreviewImg] = useState(null);
  const [proofEntry, setProofEntry] = useState(null);
  const proofInputRef = useRef(null);

  useEffect(()=>{ const h=e=>setMonth(e.detail); window.addEventListener('monthChange',h); return()=>window.removeEventListener('monthChange',h); },[]);
  useEffect(()=>{ if(!useRange){ setFromMonth(month); setToMonth(month); } },[month]);

  const { data: entries=[], isLoading } = useQuery({
    queryKey: useRange ? ['entries','range',fromMonth,toMonth] : ['entries',month],
    queryFn: () => api.get(useRange ? `/entries?fromMonth=${fromMonth}&toMonth=${toMonth}` : `/entries?month=${month}`).then(r=>r.data),
    refetchInterval: 20000,
  });
  const { data: clients=[] } = useQuery({ queryKey:['clients'], queryFn:()=>api.get('/clients').then(r=>r.data) });
  const { data: categories=[] } = useQuery({ queryKey:['categories'], queryFn:()=>api.get('/categories').then(r=>r.data) });
  const { data: dupClusters=[] } = useQuery({ queryKey:['duplicates',month], queryFn:()=>api.get(`/reports/duplicates?month=${month}`).then(r=>r.data), enabled:isOwner&&dupReview });
  const { data: corrTypes=[] } = useQuery({ queryKey:['correction-types'], queryFn:()=>api.get('/correction-types').then(r=>r.data) });
  const { data: users=[] } = useQuery({ queryKey:['users'], queryFn:()=>api.get('/users').then(r=>r.data), enabled:isOwner });

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/entries/${id}`),
    onSuccess: () => { toast.success('Entry deleted'); qc.invalidateQueries(['entries']); qc.invalidateQueries(['dashboard']); setDeleteConfirm(null); },
    onError: err => toast.error(err.response?.data?.error || 'Failed to delete — you may not have permission'),
  });
  const bulkDeleteMutation = useMutation({
    mutationFn: ids => api.post('/entries/delete-duplicates',{ids}),
    onSuccess: res => { toast.success(res.data.message); setSelectedDups([]); qc.invalidateQueries(['entries']); qc.invalidateQueries(['duplicates']); },
    onError: () => toast.error('Failed to delete duplicates'),
  });
  const corrMutation = useMutation({
    mutationFn: ({id,...d}) => api.patch(`/entries/${id}/correction`,d),
    onSuccess: () => { toast.success('Corrections updated'); qc.invalidateQueries(['entries']); qc.invalidateQueries(['dashboard']); setCorrModal(null); },
    onError: err => toast.error(err.response?.data?.error || 'Failed to update corrections'),
  });
  const editMutation = useMutation({
    mutationFn: async ({id,...d}) => {
      await api.patch(`/entries/${id}`,d);
      if (editProofFile) {
        const fd = new FormData(); fd.append('proof', editProofFile);
        await api.post(`/entries/${id}/proof`, fd);
      } else if (editProofRemoved) {
        await api.delete(`/entries/${id}/proof`);
      }
    },
    onSuccess: () => { toast.success('Entry updated'); setEditModal(null); setEditClientId(''); setEditProofFile(null); setEditProofPreview(null); setEditProofRemoved(false); qc.invalidateQueries(['entries']); qc.invalidateQueries(['myEntries']); qc.invalidateQueries(['dashboard']); },
    onError: err => toast.error(err.response?.data?.error || 'Failed to update — please try again'),
  });
  const uploadProof = useMutation({
    mutationFn: ({id,file}) => { const fd=new FormData(); fd.append('proof',file); return api.post(`/entries/${id}/proof`,fd); },
    onSuccess: () => { toast.success('Proof uploaded'); qc.invalidateQueries(['entries']); setProofEntry(null); },
    onError: err => toast.error(err.response?.data?.error || 'Upload failed — max size is 10MB'),
  });
  const removeProof = useMutation({
    mutationFn: id => api.delete(`/entries/${id}/proof`),
    onSuccess: () => { toast.success('Proof removed'); qc.invalidateQueries(['entries']); },
    onError: () => toast.error('Failed to remove proof'),
  });
  const markBilledMutation = useMutation({
    mutationFn: ({id,manually_billed}) => api.patch(`/entries/${id}/mark-billed`,{manually_billed}),
    onSuccess: (_,{manually_billed}) => { toast.success(manually_billed?'Marked as billed':'Unmarked'); qc.invalidateQueries(['entries']); },
    onError: err => toast.error(err.response?.data?.error || 'Failed to update'),
  });

  const filtered = entries.filter(e=>{
    if(filters.client&&e.client_id!==filters.client) return false;
    if(filters.category&&e.category_id!==filters.category) return false;
    if(filters.type==='dup'&&!e.is_duplicate) return false;
    if(filters.type==='correction'&&!e.corrections) return false;
    if(filters.type==='proof'&&!e.proof_image) return false;
    if(filters.search&&!e.description.toLowerCase().includes(filters.search.toLowerCase())&&!e.client_name.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  // Selecting entries to bill only makes sense for one client at a time
  const canSelect = isOwner && !!filters.client;
  const selectableIds = filtered.filter(e=>!e.invoice_id && !e.manually_billed).map(e=>e.id);
  const allSelected = selectableIds.length>0 && selectableIds.every(id=>selectedIds.includes(id));
  useEffect(()=>{ setSelectedIds([]); }, [filters.client]);
  const selectedEntries = filtered.filter(e=>selectedIds.includes(e.id));
  const selectedSubtotal = selectedEntries.reduce((s,e)=>s+(parseFloat(e.unit_price)||0)*e.quantity,0);

  function toggleSelectAll() {
    setSelectedIds(allSelected ? [] : selectableIds);
  }
  function toggleSelect(id) {
    setSelectedIds(p => p.includes(id) ? p.filter(x=>x!==id) : [...p,id]);
  }
  function createInvoiceFromSelection() {
    if (!selectedEntries.length) return;
    const items = selectedEntries.map(e => ({
      description: e.description,
      hsn_sac_code: '998391',
      quantity: e.quantity,
      unit_price: e.unit_price,
      gst_rate: e.gst_percent,
      entry_id: e.id,
      proof_image: e.proof_image || null,
    }));
    navigate('/invoices/new', { state: { client_id: filters.client, items } });
  }

  function openEdit(e) {
    setEditModal(e);
    setEditClientId(e.client_id||'');
    const client = clients.find(c=>c.id===e.client_id);
    const allNodes = client?.allNodes || [];
    const node = allNodes.find(n=>n.id===e.hierarchy_node_id);
    const personId = node ? (node.parent_id || node.id) : '';
    setEditPersonNodeId(personId);
    setEditForm({ description:e.description, quantity:e.quantity, work_date:(e.work_date||'').slice(0,10), category_id:e.category_id||'', hierarchy_node_id:e.hierarchy_node_id||'', logged_by:e.logged_by||'' });
    setEditProofFile(null);
    setEditProofPreview(e.proof_image ? `/uploads/proofs/${e.proof_image}` : null);
    setEditProofRemoved(false);
  }

  return (
    <div>
      <div className="section-head">
        <h2>Work log</h2>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{fontSize:12,color:'var(--text2)'}}>{filtered.length} entries</span>
          {isOwner&&<button className="btn-sm" onClick={()=>setDupReview(v=>!v)} style={{background:dupReview?'var(--accent-light)':''}}>
            {dupReview?'Hide duplicates':'Review duplicates'}
          </button>}
        </div>
      </div>

      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14,flexWrap:'wrap'}}>
        <button className="btn-sm" onClick={()=>setUseRange(v=>!v)} style={{background:useRange?'var(--accent-light)':'',color:useRange?'var(--accent)':'',fontWeight:useRange?600:400}}>
          📅 {useRange?'Using month range':'Use month range'}
        </button>
        {useRange && <>
          <span style={{fontSize:12,color:'var(--text2)'}}>From</span>
          <input type="month" className="form-input" value={fromMonth} onChange={e=>setFromMonth(e.target.value)} style={{width:140}}/>
          <span style={{fontSize:12,color:'var(--text2)'}}>To</span>
          <input type="month" className="form-input" value={toMonth} onChange={e=>setToMonth(e.target.value)} style={{width:140}}/>
        </>}
      </div>

      {/* Hidden file input for proof upload */}
      <input
        ref={proofInputRef} type="file" accept="image/*" style={{display:'none'}}
        onChange={e => {
          const f = e.target.files[0];
          if (f && proofEntry) { uploadProof.mutate({id:proofEntry.id,file:f}); e.target.value=''; }
        }}
      />

      {/* Duplicate review panel */}
      {isOwner&&dupReview&&(
        <div className="card" style={{marginBottom:16,border:'1px solid var(--border)'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
            <div className="card-title" style={{marginBottom:0}}>Duplicate clusters — {dupClusters.length} found</div>
            {selectedDups.length>0&&<button className="btn-primary btn-sm" style={{background:'var(--red)',borderColor:'var(--red)'}} onClick={()=>bulkDeleteMutation.mutate(selectedDups)} disabled={bulkDeleteMutation.isPending}>Delete {selectedDups.length} selected</button>}
          </div>
          {dupClusters.length===0?<div style={{fontSize:13,color:'var(--text2)'}}>No duplicates found this month ✓</div>:dupClusters.map((cluster,ci)=>(
            <div key={ci} style={{marginBottom:12,padding:10,background:'var(--surface2)',borderRadius:8}}>
              <div style={{fontWeight:600,fontSize:13,marginBottom:6}}>"{cluster[0].description}" — {cluster[0].client_name} / {cluster[0].category_name}</div>
              {cluster.map(e=>(
                <div key={e.id} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 8px',background:'var(--surface)',borderRadius:6,marginBottom:4}}>
                  <input type="checkbox" checked={selectedDups.includes(e.id)} onChange={ev=>setSelectedDups(p=>ev.target.checked?[...p,e.id]:p.filter(x=>x!==e.id))}/>
                  <span style={{flex:1,fontSize:12}}>{fmtDate(e.work_date)} · {e.logged_by_name}</span>
                  <button className="btn-danger btn-sm" onClick={()=>setDeleteConfirm(e.id)}>Delete</button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
        <input placeholder="Search description or client…" value={filters.search} onChange={e=>setFilters(f=>({...f,search:e.target.value}))} style={{flex:1,minWidth:100}}/>
        <div style={{minWidth:130}}>
          <SearchableSelect value={filters.client} onChange={id=>setFilters(f=>({...f,client:id}))}
            items={clients} allowClear clearLabel="All clients" placeholder="All clients" />
        </div>
        <div style={{minWidth:120}}>
          <SearchableSelect value={filters.category} onChange={id=>setFilters(f=>({...f,category:id}))}
            items={categories} allowClear clearLabel="All categories" placeholder="All categories" />
        </div>
        <select value={filters.type} onChange={e=>setFilters(f=>({...f,type:e.target.value}))} style={{minWidth:130}}>
          <option value="">All entries</option>
          <option value="dup">Duplicates only</option>
          <option value="correction">With corrections</option>
          <option value="proof">Has proof image</option>
        </select>
      </div>

      {isOwner && !filters.client && (
        <div style={{fontSize:12,color:'var(--text2)',marginBottom:12,padding:'8px 14px',background:'var(--surface2)',borderRadius:6}}>
          Pick a specific client above to select entries and create an invoice from them.
        </div>
      )}

      {canSelect && selectedIds.length>0 && (
        <div style={{position:'sticky',top:0,zIndex:5,display:'flex',alignItems:'center',gap:14,marginBottom:14,padding:'10px 16px',background:'var(--accent)',color:'#fff',borderRadius:8}}>
          <strong>{selectedIds.length} selected</strong>
          <span style={{opacity:0.9,fontSize:13}}>Subtotal ≈ {fmt(selectedSubtotal)} (excl. GST)</span>
          <div style={{marginLeft:'auto',display:'flex',gap:8}}>
            <button className="btn-sm" style={{background:'rgba(255,255,255,0.15)',color:'#fff',border:'none'}} onClick={()=>setSelectedIds([])}>Clear</button>
            <button className="btn-sm" style={{background:'#fff',color:'var(--accent)',fontWeight:700,border:'none'}} onClick={createInvoiceFromSelection}>📄 Create Invoice</button>
          </div>
        </div>
      )}

      <div className="tbl-wrap">
        <table>
          <thead><tr>
            {canSelect && <th style={{width:30}}><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} disabled={selectableIds.length===0}/></th>}
            <th>Date</th><th>Client</th><th>Node</th><th>Category</th><th>Description</th>
            <th>Qty</th><th>By</th><th>Proof</th><th></th>
          </tr></thead>
          <tbody>
            {isLoading?<tr><td colSpan={11}><div className="empty"><div className="spinner"/></div></td></tr>
            :filtered.length===0?<tr><td colSpan={11}><div className="empty">No entries found</div></td></tr>
            :filtered.map(e=>(
              <tr key={e.id} className={e.corrections>0?'cor-row':e.is_duplicate?'dup-row':''}>
                {canSelect && (
                  <td>
                    {e.invoice_id
                      ? <span title={`Already invoiced — ${e.invoiced_as||''}`} style={{fontSize:9,fontWeight:700,color:'#1E7E4E'}}>✓ Billed</span>
                      : e.manually_billed
                      ? <span title="Marked billed manually — not on a WorkTracker invoice" style={{fontSize:9,fontWeight:700,color:'#1E7E4E'}}>✓ Billed</span>
                      : <input type="checkbox" checked={selectedIds.includes(e.id)} onChange={()=>toggleSelect(e.id)}/>}
                  </td>
                )}
                <td style={{color:'var(--text2)',whiteSpace:'nowrap'}}>{fmtDate(e.work_date)}</td>
                <td><span className="pill pill-blue" style={{fontSize:10}}>{e.client_name}</span></td>
                <td style={{fontSize:11}}>
                  {!e.node_name ? (
                    <span style={{color:'var(--text3)'}}>—</span>
                  ) : e.parent_node_name ? (
                    // Stakeholder entry: dim parent, highlight stakeholder
                    <span>
                      <span style={{color:'var(--text3)'}}>{e.parent_node_name} ›&nbsp;</span>
                      <span style={{color:'var(--text1)',fontWeight:500}}>{e.node_name}</span>
                    </span>
                  ) : (
                    // Person-level entry: highlighted pill
                    <span style={{display:'inline-block',background:'var(--accent-light)',color:'var(--accent)',borderRadius:4,padding:'1px 7px',fontWeight:600,fontSize:10,border:'1px solid rgba(45,107,228,0.2)'}}>
                      {e.node_name}
                    </span>
                  )}
                </td>
                <td><span className="pill pill-gray">{e.category_name}</span></td>
                <td>
                  {e.description}
                  {e.is_duplicate&&<span className="pill pill-orange" style={{marginLeft:6,fontSize:9}}>dup</span>}
                  {e.corrections>0&&(
                    <span className="cor-tag" style={{marginLeft:6}}>
                      +{e.corrections} cor
                      {(e.correction_lines||[]).filter(l=>l.type_name).map((l,i)=>(
                        <span key={i} style={{marginLeft:4,fontSize:10,opacity:0.9}}>{l.type_name}×{l.count}</span>
                      ))}
                      {!(e.correction_lines||[]).some(l=>l.type_name)&&e.correction_reason?` — ${e.correction_reason.slice(0,25)}`:''}
                    </span>
                  )}
                </td>
                <td>{e.quantity}</td>
                <td style={{fontSize:11,color:'var(--text2)'}}>{e.logged_by_name?.split(' ')[0]}</td>
                {/* Proof column */}
                <td style={{whiteSpace:'nowrap'}}>
                  {e.proof_image ? (
                    <div style={{display:'flex',gap:3,alignItems:'center'}}>
                      <img
                        src={`/uploads/proofs/${e.proof_image}`}
                        alt="proof"
                        title="Click to preview"
                        style={{height:30,width:30,objectFit:'cover',borderRadius:4,border:'1px solid var(--border)',cursor:'pointer'}}
                        onClick={()=>setPreviewImg(`/uploads/proofs/${e.proof_image}`)}
                      />
                      <button className="btn-sm" title="Copy shareable link" style={{fontSize:11,padding:'2px 5px'}}
                        onClick={()=>{ navigator.clipboard.writeText(window.location.origin+`/uploads/proofs/${e.proof_image}`); toast.success('Link copied!'); }}>
                        🔗
                      </button>
                    </div>
                  ) : (
                    (isOwner||e.logged_by===user.id) && (
                      <button className="btn-sm" title="Attach proof image" style={{fontSize:11}} onClick={()=>{ setProofEntry(e); proofInputRef.current?.click(); }}>
                        📎
                      </button>
                    )
                  )}
                </td>
                {/* Actions */}
                <td style={{whiteSpace:'nowrap',textAlign:'right'}}>
                  {(isOwner||e.logged_by===user.id)&&<button className="btn-sm" style={{marginRight:3}} onClick={()=>{
                    const existing = (e.correction_lines||[]);
                    setCorrLines(existing.length ? existing.map(l=>({correction_type_id:l.correction_type_id||'',correction_count:l.count||1,reason:l.reason||''})) : [{correction_type_id:'',correction_count:e.corrections||1,reason:e.correction_reason||''}]);
                    setCorrModal(e);
                  }}>Manage corrections</button>}
                  {(isOwner||e.logged_by===user.id)&&<button className="btn-sm" style={{marginRight:3}} onClick={()=>openEdit(e)}>Edit</button>}
                  {isOwner && !e.invoice_id && (
                    <button className="btn-sm" style={{marginRight:3}}
                      title={e.manually_billed ? 'Billed outside WorkTracker — click to unmark' : "Mark as billed without creating a WorkTracker invoice (e.g. billed on the client's own PO)"}
                      onClick={()=>markBilledMutation.mutate({id:e.id, manually_billed: !e.manually_billed})}>
                      {e.manually_billed ? 'Unmark Billed' : 'Mark Billed'}
                    </button>
                  )}
                  {(isOwner||e.logged_by===user.id)&&<button className="btn-danger btn-sm" title="Delete entry" onClick={()=>setDeleteConfirm(e.id)}>🗑</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Image preview overlay */}
      {previewImg&&(
        <div className="modal-overlay" onClick={()=>setPreviewImg(null)} style={{background:'rgba(0,0,0,0.85)',cursor:'zoom-out'}}>
          <img src={previewImg} alt="Design proof" style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:8,boxShadow:'0 8px 40px rgba(0,0,0,0.5)'}} onClick={e=>e.stopPropagation()}/>
          <button style={{position:'fixed',top:16,right:16,background:'rgba(255,255,255,0.15)',color:'#fff',border:'none',borderRadius:8,padding:'6px 14px',fontSize:14,cursor:'pointer'}} onClick={()=>setPreviewImg(null)}>✕ Close</button>
        </div>
      )}

      {/* Edit entry modal */}
      {editModal && <EditModal
        entry={editModal}
        form={editForm} setForm={setEditForm}
        clientId={editClientId} setClientId={setEditClientId}
        personNodeId={editPersonNodeId} setPersonNodeId={setEditPersonNodeId}
        clients={clients} categories={categories}
        isOwner={isOwner} users={users}
        onSave={() => {
          if (!editClientId) { toast.error('Please select a client'); return; }
          if (!editForm.description) { toast.error('Description cannot be empty'); return; }
          editMutation.mutate({ id: editModal.id, client_id: editClientId, ...editForm });
        }}
        onClose={() => { setEditModal(null); setEditClientId(''); setEditProofFile(null); setEditProofPreview(null); setEditProofRemoved(false); }}
        isPending={editMutation.isPending}
        proofFile={editProofFile} setProofFile={setEditProofFile}
        proofPreview={editProofPreview} setProofPreview={setEditProofPreview}
        setProofRemoved={setEditProofRemoved}
      />}

      {/* Corrections modal */}
      {corrModal&&(
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:520}}>
            <div className="modal-title">Manage corrections</div>
            <div style={{fontSize:13,color:'var(--text2)',marginBottom:14,padding:10,background:'var(--surface2)',borderRadius:'var(--radius-sm)'}}>
              <strong>{corrModal.client_name}</strong> — {corrModal.category_name}: {corrModal.description}
            </div>
            <div style={{fontSize:11,fontWeight:600,color:'var(--text3)',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.05em'}}>Correction lines</div>
            {corrLines.map((line,i)=>{
              const selType = corrTypes.find(t=>t.id===line.correction_type_id);
              return (
                <div key={i} style={{border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'10px 12px',marginBottom:8,background:'var(--surface2)'}}>
                  <div style={{display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
                    <div style={{flex:'1 1 160px'}}>
                      <div style={{fontSize:11,color:'var(--text3)',marginBottom:4}}>Correction type</div>
                      <select value={line.correction_type_id} onChange={ev=>{const v=[...corrLines];v[i]={...v[i],correction_type_id:ev.target.value};setCorrLines(v);}}>
                        <option value="">— Untyped —</option>
                        {corrTypes.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </div>
                    <div style={{width:70}}>
                      <div style={{fontSize:11,color:'var(--text3)',marginBottom:4}}>Count</div>
                      <input type="number" min="1" value={line.correction_count} onChange={ev=>{const v=[...corrLines];v[i]={...v[i],correction_count:parseInt(ev.target.value)||1};setCorrLines(v);}}/>
                    </div>
                    {corrLines.length>1&&<button className="btn-danger btn-sm" style={{marginBottom:2}} onClick={()=>setCorrLines(corrLines.filter((_,j)=>j!==i))}>✕</button>}
                  </div>
                  <div style={{marginTop:8}}>
                    <div style={{fontSize:11,color:'var(--text3)',marginBottom:4}}>Reason (optional)</div>
                    <input value={line.reason} onChange={ev=>{const v=[...corrLines];v[i]={...v[i],reason:ev.target.value};setCorrLines(v);}} placeholder="e.g. Client requested colour change"/>
                  </div>
                </div>
              );
            })}
            <button className="btn-sm" style={{marginBottom:12}} onClick={()=>setCorrLines([...corrLines,{correction_type_id:'',correction_count:1,reason:''}])}>+ Add another correction type</button>
            {corrLines.some(l=>corrLines.length>0)&&(
              <div style={{fontSize:12,fontWeight:600,color:'var(--text1)',marginBottom:12,padding:'6px 10px',background:'var(--accent-light)',borderRadius:'var(--radius-sm)'}}>
                Total corrections: {corrLines.reduce((s,l)=>s+l.correction_count,0)}
              </div>
            )}
            <div style={{fontSize:12,color:'var(--text2)',marginBottom:12}}>Correction rows appear highlighted in red on invoices and reports.</div>
            <div className="modal-actions">
              <button onClick={()=>setCorrModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={()=>corrMutation.mutate({id:corrModal.id,correctionLines:corrLines})} disabled={corrMutation.isPending}>{corrMutation.isPending?'Saving…':'Apply'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm dialog */}
      <ConfirmDialog
        open={!!deleteConfirm}
        title="Delete entry?"
        message="This will permanently remove the entry. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={()=>deleteMutation.mutate(deleteConfirm)}
        onCancel={()=>setDeleteConfirm(null)}
      />
    </div>
  );
}
